/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import FancyScroll from "@eisinc/App/Components/FancyScroll/FancyScroll";
import NavigationPromotion from "@eisinc/App/Components/Navigation/NavigationPromotion";
import NavigationSubLevel from "@eisinc/App/Components/Navigation/NavigationSubLevel";
import { closeAllTippy } from "@eisinc/App/Utilities/helpers";
import Tooltip from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import { MainNavigationStyles, MappedLink } from "@eisinc/Overrides/Widgets/Header/MainNavigation";
import { color } from "@eisinc/XDL/Utilities";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { Clickable, Typography } from "@insite/mobius/index";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Menu from "@insite/mobius/Menu";
import Page, { PageProps } from "@insite/mobius/Page";
import Popover from "@insite/mobius/Popover";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

export interface NavigationFWStyles {
    popup?: InjectableCss;
    container: PageProps;
    firstLevel?: InjectableCss;
    itemLink?: InjectableCss;
    menuItemIcon?: IconProps;
}

export const NavigationFWStyles: NavigationFWStyles = {
    popup: {
        css: css`
            padding: 24px 0 30px;
        `,
    },
    container: {
        as: "div",
        fullWidth: [true, true, false, false, false],
        padding: 0,
        background: "transparent",
        css: css`
            height: 50vh;
            display: flex;
        `,
    },
    firstLevel: {
        css: css`
            border-right: 1px solid ${color("common.ui1")};
            width: 340px;
            min-width: 340px;
            height: 100%;
        `,
    },
    itemLink: {
        css: css`
            position: relative;
            width: 100%;
            padding: 10px 15px;
            font-size: 14px;
            transition: background-color 200ms ease;
            span {
                color: ${color("text.main")} !important;
            }
            &:hover {
                background-color: ${color("common.ui2")};
                text-decoration: none;
                span,
                p {
                    text-decoration: none !important;
                    color: ${color("text.main")} !important;
                }
            }
            ${({ opened }: any) =>
                opened &&
                `background-color: #D7D7D7; 
                span {
                    color: #424242 !important;
                }
                &:hover,&:focus{
                    background-color: #D7D7D7;
                    span {
                        color: #424242 !important;
                    }
                }`}
        `,
    },
    menuItemIcon: {
        css: css`
            position: absolute;
            right: 10px;
            top: 12px;
            margin: -3px 0 0 10px;
        `,
        size: 18,
        color: "secondary.contrast",
    },
};

const styleFW = NavigationFWStyles;

const mapStateToProps = (state: ApplicationState) => ({});

const mapDispatchToProps = {
    loadProductInfoList,
};

interface OwnProps {
    link: MappedLink;
    index: number;
    styles: MainNavigationStyles;
    container: React.RefObject<HTMLElement>;
    isOpen: boolean;
    displayChangeCustomerLink: boolean;
}

interface OwnState {
    current: number;
    link: MappedLink;
    isFullWidthOpened: boolean;
}

type Props = ThemeProps<BaseTheme> &
    OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

class MainNavigationItem extends React.Component<Props, OwnState> {
    element = React.createRef<HTMLElement>();
    popover = React.createRef<any>();
    timeoutId: number | undefined;
    realTimeTriggered = false;

    constructor(props: any) {
        super(props);

        this.state = {
            current: 0,
            link: {} as MappedLink,
            isFullWidthOpened: false,
        };
    }

    closePopover = () => {
        this.popover.current && (this.popover.current! as any).closePopover();
    };

    openPopover = () => {
        this.popover.current && (this.popover.current! as any).openPopover();
    };

    openMenu = (idx: number, link: MappedLink = {} as MappedLink) => {
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
            this.setState({ current: idx, link });
        }, 150);
        this.popover.current && (this.popover.current! as any).openPopover();
    };

    closeMenu = () => {
        clearTimeout(this.timeoutId);
    };

    closeAllPopupsHandler = (event: any) => {
        // on qty and add to cart for featured products event -> keep the overlay opened
        if (event.target.closest("input") || event.target.closest("button")) {
            return;
        }

        closeAllTippy();
        this.setState({ current: 0, link: {} as MappedLink });
    };



    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (this.props.isOpen && !prevProps.isOpen) {
            this.openPopover();
        } else if (!this.props.isOpen && prevProps.isOpen) {
            this.closePopover();
        }

        if (
            this.props.link.childrenType === "FullWidthMenu" &&
            this.props.link.children &&
            this.props.link.children?.length > 0 &&
            !this.realTimeTriggered
        ) {
            this.loadRealTimePricingForAllFeatured();
        }
    }

    loadRealTimePricingForAllFeatured() {
        this.realTimeTriggered = true;
        const productIds = this.props.link.children?.reduce((result: string[], current) => {
            if (!current?.excludeFromNavigation) {
                (current.properties?.featuredProducts ? JSON.parse(current.properties?.featuredProducts) : []).forEach(
                    (item: any) => result.indexOf(item.id) === -1 && result.push(item.id),
                );
            }
            return result;
        }, []);
        // <xng-change> MIDPT-110: adding expansions to get fully api result for products
        this.props.loadProductInfoList({
            id: `navigation`,
            getProductCollectionParameter: {
                productIds,
                expand: [
                    "detail",
                    "specifications",
                    "content",
                    "images",
                    "documents",
                    "attributes",
                    "variantTraits",
                    "properties",
                ],
                includeAttributes: ["includeOnProduct"],
            },
        });
    }

    componentWillUnmount() {
        clearTimeout(this.timeoutId);
    }

    render() {
        const {
            styles,
            link,
            index,
            isOpen,
            theme: {
                breakpoints: { maxWidths },
            },
        } = this.props;
        const downIcon = <StyledIcon src={ChevronDown} {...styles.menuItemIcon} />;
        const rightIcon = <StyledIcon src={ChevronRight} {...styleFW.menuItemIcon} />;
        const hasChildren = link.children && link.children.filter(child => !child.excludeFromNavigation).length > 0;
        const isCascading = hasChildren && link.childrenType === "CascadingMenu";
        const isMega = hasChildren && link.childrenType === "MegaMenu";
        const isFullWidth = hasChildren && link.childrenType === "FullWidthMenu";
        const hasInternalChildren = link.hasInternalChildren;

        let menuLink = (props: any = {}) => (
            <Link
                className="menu-item"
                typographyProps={styles.menuItemTypography}
                color={styles.menuItemTypography?.color}
                {...styles.menuItem}
                href={link.url}
                target={link.openInNewWindow ? "_blank" : ""}
                onMouseLeave={e => this.closeAllPopupsHandler(e)}
                onClick={(e: any) => this.closeAllPopupsHandler(e)}
            >
                {link.title}
                {(isCascading || isFullWidth || hasInternalChildren) && downIcon}
            </Link>
        );
        if (isMega || link.url === "") {
            menuLink = (props: any = {}) => (
                <StyledWrapper
                    onClick={e => this.closeAllPopupsHandler(e)}
                    onMouseLeave={e => this.closeAllPopupsHandler(e)}
                >
                    <Link
                        className="menu-item"
                        typographyProps={styles.menuItemTypography}
                        color={styles.menuItemTypography?.color}
                        {...styles.menuItem}
                        {...props}
                        {...(link.titleLink && link.titleLink.url ? { href: link.titleLink.url } : "")}
                    >
                        {link.title}
                        {(isCascading || isMega || isFullWidth || hasInternalChildren) && downIcon}
                    </Link>
                </StyledWrapper>
            );
        }

        let menuItem = menuLink;

        if ((isCascading || isFullWidth || hasInternalChildren) && link.children) {
            const menuProps: { maxDepth?: number } = {};
            if (link.maxDepth) {
                menuProps.maxDepth = link.maxDepth;
            }
            if (isFullWidth) {
                menuItem = () => (
                    <Tooltip
                        passAsChildren={true}
                        triggerComponent={
                            <Clickable
                                className="menu-item"
                                color={styles.menuItemTypography?.color}
                                {...styles.menuItem}
                                href={link.url || link.titleLink?.url}
                                {...{ isOpen: this.state.isFullWidthOpened }}
                                target={link.openInNewWindow ? "_blank" : ""}
                                onClick={(e: any) => this.closeAllPopupsHandler(e)}
                            >
                                <Typography {...styles.menuItemTypography}>
                                    {link.title}
                                    {(isCascading || isFullWidth || hasInternalChildren) && downIcon}
                                </Typography>
                            </Clickable>
                        }
                        onShow={() => { this.setState({ ...this.state, isFullWidthOpened: true }) }}
                        onHide={() => { this.setState({ ...this.state, isFullWidthOpened: false }) }}
                        placement="bottom-end"
                        delay={[100, 350]}
                        offset={[0, 0]}
                        showBlackArrow={true}
                        {...styleFW.popup}
                        cssOverrides={{
                            tooltipContainer: css`
                                max-width: none;
                                border-radius: 0;
                                margin-left: -5px;
                                width: 99vw;
                                padding-top: 24px;
                                padding-bottom: 30px
                            `,
                        }}
                        onClose={() => this.openMenu(0)}
                    >
                        <Page {...styleFW.container}>
                            <StyledWrapper {...styleFW.firstLevel}>
                                <FancyScroll showOverlay={true} bottomOnly={true} clickable={true}>
                                    {link.children
                                        ?.filter(item => !item.excludeFromNavigation)
                                        .map((item, idx) => {
                                            const hasChildren = item.children?.length;
                                            return (
                                                <Link
                                                    key={`popupLink_${item.title}`}
                                                    typographyProps={{ color: "secondary.contrast" }}
                                                    color="black"
                                                    href={item.url}
                                                    onClick={(e: any) => this.closeAllPopupsHandler(e)}
                                                    onMouseOver={() => this.openMenu(idx + 1, item)}
                                                    onMouseOut={() => this.closeMenu()}
                                                    {...({ opened: idx + 1 === this.state.current } as any)}
                                                    {...styleFW.itemLink}
                                                >
                                                    {item.title}
                                                    {!!hasChildren && rightIcon}
                                                </Link>
                                            );
                                        })}
                                </FancyScroll>
                            </StyledWrapper>
                            <StyledWrapper
                                css={css`
                                        display: flex;
                                        flex: 1;
                                    `}
                            >
                                {/** EIS-1098: show category description on sub level not the promotion one */}
                                {this.state.current ? (
                                    <NavigationSubLevel link={this.state.link} closeAllPopups={closeAllTippy} />
                                ) : (
                                    <NavigationPromotion />
                                )}
                            </StyledWrapper>
                        </Page>
                    </Tooltip>
                );
            } else if (isCascading || hasInternalChildren) {
                menuItem = () => (
                    <Menu
                        className="cascading-menu"
                        data-test-selector={`mainNavigationItem_${index}`}
                        isOpen={isOpen || undefined}
                        descriptionId={`${link.title}_${index}`}
                        menuItems={link.children as any}
                        menuTrigger={menuLink() as any}
                        displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                        {...menuProps}
                        {...styles.cascadingMenu}
                    />
                );
            }
        }

        if (isMega) {
            menuItem = () => (
                <Popover
                    className="mega-menu"
                    zIndexKey="menu"
                    {...styles.megaMenu}
                    contentBodyProps={{
                        ...styles.megaMenu?.contentBodyProps,
                        _width: 1140,
                        as: "div",
                    }}
                    controlsId={`${index}_${link.title}`}
                    ref={this.popover}
                    positionFunction={(element: React.RefObject<HTMLUListElement>) => {
                        if (this.props.container.current?.offsetParent) {
                            const parentPosition = this.props.container.current.getBoundingClientRect();
                            const rect = element.current?.getBoundingClientRect();
                            const top = rect ? rect.top + (rect.height > 100 ? styles.menuHeight : rect.height) : 0;
                            const cartWidth = 150;
                            const breakpointMaxWidth = maxWidths.find((value: number, index: number) => {
                                return (
                                    parentPosition.width + cartWidth <= value &&
                                    parentPosition.width + cartWidth > maxWidths[index - 1]
                                );
                            });

                            const width = breakpointMaxWidth ? breakpointMaxWidth - 30 : parentPosition.width;

                            return {
                                top,
                                position: "fixed",
                                left: parentPosition.left,
                                width: `${width}px`,
                            };
                        }
                        return { position: "absolute" };
                    }}
                    popoverTrigger={menuLink()}
                    insideRefs={[this.element]}
                >
                    <GridContainer
                        className="mega-menu-grid-container"
                        data-test-selector={`mainNavigationItem_${index}`}
                        {...styles.megaMenuGridContainer}
                        offsetProps={{ ...styles.megaMenuGridContainer?.offsetProps, as: "ul" }}
                    >
                        {link
                            .children!.filter(child => !child.excludeFromNavigation)
                            .map((child, index) => (
                                <GridItem
                                    className="mega-menu-grid-item"
                                    width={(12 / link.numberOfColumns!) as GridWidths}
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    {...styles.megaMenuGridItem}
                                    as="li"
                                >
                                    <Link
                                        className="mega-menu-heading-link"
                                        {...styles.megaMenuHeading}
                                        href={child.url}
                                        onClick={this.closePopover}
                                    >
                                        {child.title}
                                    </Link>
                                    {child.children && (
                                        <ul data-test-selector={`linksFor_${child.title}`}>
                                            {child.children
                                                .filter(grandChild => {
                                                    if (grandChild.excludeFromNavigation) {
                                                        return false;
                                                    }
                                                    if (
                                                        grandChild.type === "ChangeCustomerPage" &&
                                                        !this.props.displayChangeCustomerLink
                                                    ) {
                                                        return false;
                                                    }
                                                    if (grandChild.type === "VmiUsersPage") {
                                                        const currentMode = getCookie(NavigationModeCookieName);
                                                        if (!currentMode || currentMode === "Storefront") {
                                                            return false;
                                                        }
                                                    }

                                                    return true;
                                                })
                                                .map((grandChild, index) => (
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    <li key={index}>
                                                        <Link
                                                            className="mega-menu-link"
                                                            {...styles.megaMenuLink}
                                                            onClick={this.closePopover}
                                                            href={grandChild.url}
                                                            data-test-selector={grandChild.title}
                                                        >
                                                            {grandChild.title}
                                                        </Link>
                                                    </li>
                                                ))}
                                        </ul>
                                    )}
                                </GridItem>
                            ))}
                    </GridContainer>
                </Popover>
            );
        }

        return menuItem();
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(MainNavigationItem));
